import { NavLink, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';

import {
  LayoutDashboard,
  TrendingUp,
  Users,
  Wallet,
  BarChart3,
  FileText,
  Settings,
  ArrowDownToLine,
  ArrowUpToLine,
  ShieldCheck,
  Shield,
  Puzzle,
  UsersRound,
  Code2,
  X,
  Brain,
  Bell,
  ChevronDown,
  ChevronRight,
  UserCheck,
  ClipboardList,
  Globe,
  CreditCard,
  Mail,
  Layers,
} from 'lucide-react';

import { useLocale } from '../../contexts/LocaleContext';
import { pluginsAPI } from '../../services/api';

// ── Cascade priority tooltip ──────────────────────────────────────────────
const CASCADE_TIPS = (t) => ({
  clients: {
    priority: 1,
    label: 'Highest Priority',
    color: '#ef4444',
    desc: 'User-level overrides beat every other layer. Set here for VIP clients or restricted accounts.',
  },
  groups: {
    priority: 2,
    label: 'Group Level',
    color: '#f97316',
    desc: 'Applies to all clients in the group. Overrides symbol & platform defaults.',
  },
  instruments: {
    priority: 3,
    label: 'Symbol Level',
    color: '#3b82f6',
    desc: 'Per-instrument pricing and execution rules. Falls back to platform defaults if empty.',
  },
});

function CascadeBadge({ tipKey }) {
  const { t } = useLocale();
  const tips = CASCADE_TIPS(t);
  const tip = tips[tipKey];
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0 });

  const handleEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({ top: rect.top });
    }
    setShow(true);
  };

  return (
    <span
      ref={ref}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
    >
      {/* Badge */}
      <span
        style={{
          fontSize: '10px',
          fontWeight: '700',
          padding: '2px 6px',
          borderRadius: '20px',
          background: `${tip.color}18`,
          color: tip.color,
          border: `1px solid ${tip.color}35`,
          letterSpacing: '0.04em',
          cursor: 'default',
          userSelect: 'none',
        }}
      >
        P{tip.priority}
      </span>

      {/* Tooltip portal-style via fixed positioning */}
      {show && (
        <div
          style={{
            position: 'fixed',
            left: '268px',
            top: `${pos.top - 10}px`,
            zIndex: 9999,
            pointerEvents: 'none',
            animation: 'cascadeTipIn 0.15s ease',
          }}
        >
          <div
            style={{
              background: '#0f172a',
              border: `1px solid ${tip.color}35`,
              borderRadius: '12px',
              padding: '12px 14px',
              width: '230px',
              boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${tip.color}15`,
            }}
          >
            {/* Arrow */}
            <div style={{
              position: 'absolute',
              left: '-6px',
              top: '18px',
              width: '10px',
              height: '10px',
              background: '#0f172a',
              border: `1px solid ${tip.color}35`,
              borderRight: 'none',
              borderTop: 'none',
              transform: 'rotate(45deg)',
            }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '7px' }}>
              <span style={{
                fontSize: '10px', fontWeight: '700',
                padding: '2px 8px', borderRadius: '20px',
                background: `${tip.color}18`, color: tip.color,
                border: `1px solid ${tip.color}40`,
                letterSpacing: '0.05em',
              }}>
                P{tip.priority} · {tip.label}
              </span>
            </div>

            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', margin: '0 0 8px', lineHeight: '1.5' }}>
              {tip.desc}
            </p>

            <div style={{
              fontSize: '11px',
              color: `${tip.color}bb`,
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              paddingTop: '7px',
              borderTop: '1px solid rgba(255,255,255,0.06)',
            }}>
              <span>💡</span>
              <span>{t('sidebar.cascadeTipFallthrough')}</span>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}

// ── Collapsible nav group ──────────────────────────────────────────────────
function NavGroup({ icon: Icon, label, badge, children, defaultOpen = false }) {
  const location = useLocation();
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    const childPaths = children
      .map(c => c.props?.to || c.props?.path || '')
      .filter(Boolean);
    if (childPaths.some(p => location.pathname.startsWith(p))) {
      setOpen(true);
    }
  }, [location.pathname]); // eslint-disable-line

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-smooth text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]"
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        <div className="flex-1 min-w-0 flex items-center gap-2 text-left">
          <span className="font-medium truncate">{label}</span>
          {badge && (
            <span className={`px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0 ${badge.className}`}>
              {badge.text}
            </span>
          )}
        </div>
        {open
          ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
          : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
        }
      </button>

      {open && (
        <div className="ml-4 pl-3 border-l border-[var(--border-color)] mt-0.5 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Child nav link (indented) ──────────────────────────────────────────────
function ChildNavLink({ to, icon: Icon, label, onClick }) {
  return (
    <NavLink
      to={to}
      end
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-smooth ${
          isActive
            ? 'bg-[var(--accent-primary)] text-white'
            : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
        }`
      }
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="font-medium truncate">{label}</span>
    </NavLink>
  );
}

export default function Sidebar({ isOpen, isMobile, onClose }) {
  const { t } = useLocale();
  const [plugins, setPlugins] = useState([]);
  const [hasApiAccess, setHasApiAccess] = useState(false);

  useEffect(() => {
    pluginsAPI.getAll()
      .then(res => setPlugins(res.purchased || []))
      .catch(err => { console.error('Failed to load plugins:', err); setPlugins([]); });
  }, []);

  useEffect(() => {
    fetch('/api/broker/api-keys/access', {
      headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.hasAccess) setHasApiAccess(true);
      })
      .catch(() => {});
  }, []);

  const isPluginActive = (key) =>
    plugins.some(p => (p.plugin_key === key || p.pluginKey === key) && p.is_active);

  const kycEnabled               = isPluginActive('kyc_verification');
  const advancedAnalyticsEnabled = isPluginActive('advanced_analytics');
  const pagesEnabled             = isPluginActive('pages');
  const creditsEnabled           = isPluginActive('credits');
  const emailTemplatesEnabled    = isPluginActive('email_templates');

  const handleNavClick = () => { if (isMobile) onClose(); };

  // ── Reusable nav link ────────────────────────────────────────────────────
  const Item = ({ path, icon: Icon, label, badge, description, end: isEnd, cascadeTip }) => (
    <NavLink
      to={path}
      end={isEnd ?? path === '/'}
      onClick={handleNavClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-smooth ${
          isActive
            ? 'bg-[var(--accent-primary)] text-white'
            : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className="w-4.5 h-4.5 w-[18px] h-[18px] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">{label}</span>
              {cascadeTip && !isActive && <CascadeBadge tipKey={cascadeTip} />}
              {badge && !cascadeTip && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 leading-none ${isActive ? 'bg-white/20 text-white' : badge.className}`}>
                  {badge.text}
                </span>
              )}
            </div>
            {description && !isActive && (
              <p className="text-[11px] text-[var(--text-tertiary)] truncate mt-0.5">{description}</p>
            )}
          </div>
        </>
      )}
    </NavLink>
  );

  const Section = ({ label }) => (
    <p className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-[var(--text-tertiary)] select-none">
      {label}
    </p>
  );

  const NavItems = () => (
    <nav
      className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto min-h-0"
      style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--border-color) transparent' }}
    >
      <style>{`
        @keyframes cascadeTipIn {
          from { opacity: 0; transform: translateX(-6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* ════════════════════════════════════
          OVERVIEW
      ════════════════════════════════════ */}
      <Section label={t('sidebar.sectionOverview')} />
      <Item path="/" icon={LayoutDashboard} label={t('nav.dashboard')} />

      {/* ════════════════════════════════════
          MANAGEMENT
      ════════════════════════════════════ */}
      <Section label={t('sidebar.sectionManagement')} />
      <Item
        path="/clients"
        icon={Users}
        label={t('nav.clients')}
        cascadeTip="clients"
        description={t('sidebar.descClients')}
      />
      <Item
        path="/account-groups"
        icon={UsersRound}
        label={t('sidebar.accountGroups')}
        cascadeTip="groups"
        description={t('sidebar.descGroups')}
      />
      <Item
        path="/instruments"
        icon={TrendingUp}
        label={t('nav.instruments')}
        cascadeTip="instruments"
        description={t('sidebar.descInstruments')}
      />
      <Item
        path="/cascade-settings"
        icon={Layers}
        label={t('sidebar.cascadeGuide')}
        badge={{ text: t('sidebar.badgeHowItWorks'), className: 'bg-[var(--accent-info)]/10 text-[var(--accent-info)]' }}
        description={t('sidebar.descCascade')}
      />
      <Item
        path="/accounts"
        icon={Wallet}
        label={t('nav.accounts')}
        badge={{ text: t('sidebar.badgeLive'), className: 'bg-[var(--accent-info)]/20 text-[var(--accent-info)]' }}
        description={t('sidebar.descAccounts')}
      />

      {/* ════════════════════════════════════
          TRADING
      ════════════════════════════════════ */}
      <Section label={t('sidebar.sectionTrading')} />
      <Item
        path="/trading"
        icon={BarChart3}
        label={t('nav.trading')}
        badge={{ text: t('sidebar.badgeLive'), className: 'bg-[var(--accent-info)]/20 text-[var(--accent-info)]' }}
      />
      <Item path="/reports" icon={FileText} label={t('nav.reports')} />
      {advancedAnalyticsEnabled && (
        <Item
          path="/analytics"
          icon={Brain}
          label={t('sidebar.advancedAnalytics')}
          badge={{ text: t('sidebar.badgePro'), className: 'bg-[var(--accent-primary)]/20 text-[var(--accent-primary)]' }}
          description={t('sidebar.descAnalytics')}
        />
      )}

      {/* ════════════════════════════════════
          FINANCE
      ════════════════════════════════════ */}
      <Section label={t('sidebar.sectionFinance')} />
      <Item path="/deposits"    icon={ArrowDownToLine} label={t('nav.deposits')}    />
      <Item path="/withdrawals" icon={ArrowUpToLine}   label={t('nav.withdrawals')} />

      {/* ════════════════════════════════════
          PLUGINS
      ════════════════════════════════════ */}
      <Section label={t('sidebar.sectionPlugins')} />
      <Item
        path="/plugins"
        icon={Puzzle}
        label={t('sidebar.pluginStore')}
        badge={{ text: t('sidebar.badgeAddon'), className: 'bg-gradient-to-r from-orange-500 to-red-500 text-white animate-pulse' }}
      />

      {kycEnabled && (
        <NavGroup
          icon={Shield}
          label={t('sidebar.kycVerification')}
          badge={{ text: t('sidebar.badgeActive'), className: 'bg-[var(--accent-success)]/20 text-[var(--accent-success)]' }}
        >
          <ChildNavLink to="/broker/kyc"               icon={ClipboardList} label={t('sidebar.kycDashboard')} onClick={handleNavClick} />
          <ChildNavLink to="/plugins/kyc_verification" icon={Settings}      label={t('sidebar.kycConfig')}    onClick={handleNavClick} />
        </NavGroup>
      )}

      {pagesEnabled && (
        <Item
          path="/plugins/pages"
          icon={Globe}
          label={t('sidebar.pages')}
          badge={{ text: t('sidebar.badgeActive'), className: 'bg-[var(--accent-success)]/20 text-[var(--accent-success)]' }}
        />
      )}
      {creditsEnabled && (
        <Item
          path="/plugins/credits"
          icon={CreditCard}
          label={t('sidebar.credits')}
          badge={{ text: t('sidebar.badgeActive'), className: 'bg-[var(--accent-success)]/20 text-[var(--accent-success)]' }}
        />
      )}
      {emailTemplatesEnabled && (
        <Item
          path="/plugins/email_templates"
          icon={Mail}
          label={t('sidebar.emailTemplates')}
          badge={{ text: t('sidebar.badgeActive'), className: 'bg-[var(--accent-success)]/20 text-[var(--accent-success)]' }}
        />
      )}

      {/* ════════════════════════════════════
          SYSTEM
      ════════════════════════════════════ */}
      <Section label={t('sidebar.sectionSystem')} />
      <Item path="/audit-log" icon={ShieldCheck} label={t('nav.auditLog')} />
      <Item
        path="/notifications"
        icon={Bell}
        label={t('nav.notifications')}
        badge={{ text: t('sidebar.badgeConfig'), className: 'bg-[var(--accent-info)]/20 text-[var(--accent-info)]' }}
      />
      {hasApiAccess && (
        <Item
          path="/api-docs"
          icon={Code2}
          label={t('sidebar.restApi')}
          badge={{ text: t('sidebar.badgeApi'), className: 'bg-[var(--accent-info)]/20 text-[var(--accent-info)]' }}
        />
      )}
      <Item path="/settings" icon={Settings} label={t('nav.settings')} />
    </nav>
  );

  // ── MOBILE — fixed slide-in drawer ────────────────────────────────────────
  if (isMobile) {
    return (
      <aside
        className={`
          fixed inset-y-0 left-0 z-30 w-72
          bg-[var(--bg-secondary)] border-r border-[var(--border-color)]
          flex flex-col min-h-0 shadow-2xl
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ height: '100dvh' }}
      >
        <div className="h-16 flex items-center justify-between px-6 border-b border-[var(--border-color)] flex-shrink-0">
          <h1 className="text-xl font-bold text-gradient">Broker Admin</h1>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-smooth"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <NavItems />
      </aside>
    );
  }

  // ── DESKTOP — collapsible in-flow sidebar ─────────────────────────────────
  return (
    <aside
      style={{ width: isOpen ? '256px' : '0px', minWidth: 0 }}
      className="bg-[var(--bg-secondary)] border-r border-[var(--border-color)] flex flex-col flex-shrink-0 overflow-hidden transition-all duration-300 ease-in-out h-full"
    >
      <div className="w-64 flex flex-col h-full min-h-0">
        <div className="h-16 flex items-center px-6 border-b border-[var(--border-color)] flex-shrink-0">
          <h1 className="text-xl font-bold text-gradient">Broker Admin</h1>
        </div>
        <NavItems />
      </div>
    </aside>
  );
}